import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { fetchEventDetails } from '../services/strapiService';
import { useEventImages } from '../components/map/hooks/useEventImages';
import { useEventInterest } from '../components/map/hooks/useEventInterest';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0;
  min-height: 100vh;
  background: #FFFACD; /* Giallo chiaro di sfondo */

  @media (max-width: 768px) {
    padding: 0;
    max-width: 100%;
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg,rgb(250, 250, 205) 0%,rgb(219, 184, 134) 100%); /* Gradiente arancione */
  color: white;
  padding: 30px 20px;
  text-align: center;
`;

const Logo = styled.h1`
  font-size: 32px;
  margin: 0 0 10px 0;
  font-weight: bold;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  margin: 0;
  opacity: 0.95;
  font-size: 16px;
  color: white;
`;

const EventCard = styled.div`
  background: white;
  margin: -20px 20px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(255, 140, 0, 0.2); /* Ombra arancione */
  overflow: hidden;
  border: 2px solid #FFE4B5; /* Bordo giallo chiaro */

  @media (max-width: 768px) {
    margin: -20px 10px 20px;
  }
`;

const EventImage = styled.div`
  width: 100%;
  height: 300px;
  background: ${props => props.image ? `url(${props.image})` : 'linear-gradient(135deg, #FF8C00 0%, #FFB347 100%)'};
  background-size: cover;
  background-position: center;
  position: relative;
  cursor: ${props => props.image ? 'pointer' : 'default'}; /* Cursore pointer se c'è un'immagine */
  transition: transform 0.2s ease;

  &:hover {
    transform: ${props => props.image ? 'scale(1.02)' : 'none'}; /* Leggero zoom al hover */
  }

  @media (max-width: 768px) {
    height: 250px;
  }
`;

const EventContent = styled.div`
  padding: 30px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const EventTitle = styled.h1`
  font-size: 28px;
  margin: 0 0 20px 0;
  color: #FF6B00; /* Arancione scuro per il titolo */
  line-height: 1.3;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin: 30px 0;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 15px;
  background: #FFF8DC; /* Giallo molto chiaro */
  border-radius: 8px;
  border-left: 4px solid #FF8C00; /* Bordo arancione */
  transition: all 0.2s ease;

  &:hover {
    background: #FFEFDB; /* Giallo più scuro al hover */
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 140, 0, 0.15);
  }
`;

const InfoIcon = styled.div`
  font-size: 20px;
  margin-top: 2px;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-weight: 600;
  color: #FF6B00; /* Arancione scuro per le etichette */
  margin-bottom: 4px;
  font-size: 14px;
`;

const InfoValue = styled.div`
  color: #8B4513; /* Marrone caldo per i valori */
  font-size: 16px;
  line-height: 1.4;
`;

const Description = styled.div`
  font-size: 16px;
  line-height: 1.6;
  color: #8B4513; /* Marrone caldo per il testo */
  margin: 30px 0;
  padding: 20px;
  background: #FFF8DC; /* Giallo molto chiaro */
  border-radius: 8px;
  border: 1px solid #FFE4B5; /* Bordo giallo chiaro */
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  margin: 30px 0;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MainButton = styled.button`
  background: linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%); /* Gradiente arancione */
  color: white;
  border: none;
  border-radius: 10px;
  padding: 15px 30px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s;
  flex: 1;
  justify-content: center;
  min-height: 50px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);

  &:hover {
    background: linear-gradient(135deg, #FF7F00 0%, #FF6B00 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 140, 0, 0.4);
  }
`;

const SecondaryButton = styled.button`
  background: white;
  color: #FF8C00; /* Testo arancione */
  border: 2px solid #FF8C00; /* Bordo arancione */
  border-radius: 10px;
  padding: 15px 30px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s;
  flex: 1;
  justify-content: center;
  min-height: 50px;

  &:hover {
    background: #FF8C00;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 140, 0, 0.3);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 18px;
  color: #FF6B00; /* Arancione scuro */
  background: #FFFACD; /* Sfondo giallo chiaro */
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  padding: 20px;
  text-align: center;
  background: #FFFACD; /* Sfondo giallo chiaro */
  color: #FF6B00; /* Testo arancione scuro */
`;

const Footer = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #8B4513; /* Marrone caldo */
  font-size: 14px;
  background: #FFF8DC; /* Giallo molto chiaro */
  border-top: 2px solid #FFE4B5; /* Bordo giallo chiaro */
`;

const LogoImage = styled.img`
  height: 60px;
  width: auto;
  margin-bottom: 10px;
`;

// Componenti per il modal dell'immagine
const ImageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  cursor: pointer;
`;

const ModalImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 140, 0, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 140, 0, 1);
    transform: scale(1.1);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(255, 140, 0, 0.9);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${EventImage}:hover & {
    opacity: 1;
  }
`;

const EventoCondiviso = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null); // Stato per il modal

  // Usa gli hook per immagini e interesse
  const { eventImages } = useEventImages(event);
  const { isInterested, interestCount } = useEventInterest(event?.id);

  useEffect(() => {
    loadEventDetails(id);
  }, [id]);

  const loadEventDetails = async (eventId) => {
    try {
      setLoading(true);
      console.log('Caricando evento con ID:', eventId);
      
      const eventData = await fetchEventDetails(eventId);
      console.log('Dati evento ricevuti:', eventData);
      
      setEvent(eventData);
      setError(null);
    } catch (error) {
      console.error('Errore nel caricamento evento:', error);
      setError('Evento non trovato o errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // Funzione per ottenere la prima immagine
  const getMainImage = () => {
    if (eventImages && eventImages.length > 0) {
      return eventImages[0].url;
    }
    return null;
  };

  // Funzione per aprire il modal dell'immagine
  const handleImageClick = () => {
    const imageUrl = getMainImage();
    if (imageUrl) {
      setSelectedImage(imageUrl);
    }
  };

  // Funzione per chiudere il modal
  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  // Gestisce il click sull'overlay per chiudere il modal
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseModal();
    }
  };

  // Gestisce il tasto Esc per chiudere il modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && selectedImage) {
        handleCloseModal();
      }
    };

    if (selectedImage) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  const handleGoToMap = () => {
    window.location.href = 'https://finday.it';
  };

  const handleShare = async () => {
    const eventUrl = window.location.href;
    const eventTitle = event?.title || 'Evento interessante';
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: eventTitle,
          text: `Scopri questo evento su Finday!`,
          url: eventUrl,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          navigator.clipboard.writeText(eventUrl).then(() => {
            alert('Link copiato negli appunti!');
          });
        }
      }
    } else {
      navigator.clipboard.writeText(eventUrl).then(() => {
        alert('Link copiato negli appunti!');
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Data da definire';
    try {
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: it });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(timeString);
      const timeFormatted = format(date, 'HH:mm');
      
      // Se l'orario è 00:00, mostra "Tutto il giorno"
      if (timeFormatted === '00:00') {
        return 'Tutto il giorno';
      }
      
      return timeFormatted;
    } catch {
      return timeString;
    }
  };

  // Nuova funzione per gestire i range di date
  const formatDateRange = (startDate, endDate) => {
    if (startDate && endDate && startDate !== endDate) {
      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    
    return formatDate(startDate);
  };


  if (loading) {
    return (
      <LoadingContainer>
        <div>Caricamento evento...</div>
      </LoadingContainer>
    );
  }

  if (error || !event) {
    return (
      <ErrorContainer>
        <h2>😕 Evento non trovato</h2>
        <p>L'evento che stai cercando non esiste o non è più disponibile.</p>
        <MainButton onClick={handleGoToMap}>
          🗺️ Scopri altri eventi su Finday
        </MainButton>
      </ErrorContainer>
    );
  }

  return (
    <>
      <PageContainer>
        <Header>
          <LogoImage src="/logo.png" alt="Finday Logo" />
        </Header>

        <EventCard>
          <EventImage 
            image={getMainImage()}
            onClick={handleImageClick}
          >
            {getMainImage() && (
              <ImageOverlay>
                🔍 Clicca per ingrandire
              </ImageOverlay>
            )}
          </EventImage>
          
          <EventContent>
            <EventTitle>{event?.title}</EventTitle>

            <InfoGrid>
              <InfoItem>
                <InfoIcon>📅</InfoIcon>
                <InfoContent>
                  <InfoLabel>Data</InfoLabel>
                  <InfoValue>
                    {formatDateRange(event.startDate, event.endDate)}
                  </InfoValue>
                </InfoContent>
              </InfoItem>


              <InfoItem>
                <InfoIcon>🕐</InfoIcon>
                <InfoContent>
                  <InfoLabel>Orario</InfoLabel>
                  <InfoValue>
                    {event.startDate ? formatTime(event.startDate) : 'Orario da definire'}
                  </InfoValue>
                </InfoContent>
              </InfoItem>



              <InfoItem>
                <InfoIcon>📍</InfoIcon>
                <InfoContent>
                  <InfoLabel>Luogo</InfoLabel>
                  <InfoValue>
                    {event.location || 'Luogo da definire'}
                    {event.address && event.address !== event.location && (
                      <div style={{ fontSize: '14px', color: '#8B4513', marginTop: '4px' }}>
                        {event.address}
                      </div>
                    )}
                  </InfoValue>
                </InfoContent>
              </InfoItem>

              <InfoItem>
                <InfoIcon>💰</InfoIcon>
                <InfoContent>
                  <InfoLabel>Prezzo</InfoLabel>
                  <InfoValue>
                    {event.ticketPrice === 0 || event.ticketPrice === '0' || event.price === 0 || event.is_free 
                      ? '' 
                      : event.ticketPrice 
                        ? `€${event.ticketPrice}` 
                        : event.price
                          ? `€${event.price}`
                          : 'Prezzo da definire'
                    }
                  </InfoValue>
                </InfoContent>
              </InfoItem>

              {(event.organizers?.[0] || event.alternativeOrganizer) && (
                <InfoItem>
                  <InfoIcon>👥</InfoIcon>
                  <InfoContent>
                    <InfoLabel>Organizzatore</InfoLabel>
                    <InfoValue>
                      {event.organizers?.[0]?.name || event.alternativeOrganizer || 'Organizzatore da definire'}
                    </InfoValue>
                  </InfoContent>
                </InfoItem>
              )}

              {event.categories && event.categories.length > 0 && (
                <InfoItem>
                  <InfoIcon>🏷️</InfoIcon>
                  <InfoContent>
                    <InfoLabel>Categoria</InfoLabel>
                    <InfoValue>
                      {event.categories.map(cat => cat.name).join(', ')}
                    </InfoValue>
                  </InfoContent>
                </InfoItem>
              )}

              {interestCount > 0 && (
                <InfoItem>
                  <InfoIcon>❤️</InfoIcon>
                  <InfoContent>
                    <InfoLabel>Interesse</InfoLabel>
                    <InfoValue>
                      {interestCount} {interestCount === 1 ? 'persona interessata' : 'persone interessate'}
                    </InfoValue>
                  </InfoContent>
                </InfoItem>
              )}
            </InfoGrid>

            {event.description && (
              <Description>
                <strong style={{ color: '#FF6B00' }}>Descrizione:</strong>
                <div style={{ marginTop: '10px' }}>
                  {event.description}
                </div>
              </Description>
            )}

            <ButtonContainer>
              <MainButton onClick={handleGoToMap}>
                🗺️ Vedi sulla Mappa di Finday
              </MainButton>
              
              <SecondaryButton onClick={handleShare}>
                📤 Condividi Evento
              </SecondaryButton>
            </ButtonContainer>

            {(event.url || event.email || event.phone) && (
              <InfoGrid style={{ marginTop: '30px' }}>
                {event.url && (
                  <InfoItem>
                    <InfoIcon>🌐</InfoIcon>
                    <InfoContent>
                      <InfoLabel>Sito Web</InfoLabel>
                      <InfoValue>
                        <a 
                          href={event.url.startsWith('http') ? event.url : `https://${event.url}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#FF8C00', textDecoration: 'none' }}
                        >
                          Visita il sito
                        </a>
                      </InfoValue>
                    </InfoContent>
                  </InfoItem>
                )}

                {event.email && (
                  <InfoItem>
                    <InfoIcon>📧</InfoIcon>
                    <InfoContent>
                      <InfoLabel>Email</InfoLabel>
                      <InfoValue>
                        <a 
                          href={`mailto:${event.email}`}
                          style={{ color: '#FF8C00', textDecoration: 'none' }}
                        >
                          {event.email}
                        </a>
                      </InfoValue>
                    </InfoContent>
                  </InfoItem>
                )}

                {event.phone && (
                  <InfoItem>
                    <InfoIcon>📞</InfoIcon>
                    <InfoContent>
                      <InfoLabel>Telefono</InfoLabel>
                      <InfoValue>
                        <a 
                          href={`tel:${event.phone}`} 
                          style={{ color: '#FF8C00', textDecoration: 'none' }}
                        >
                          {event.phone}
                        </a>
                      </InfoValue>
                    </InfoContent>
                  </InfoItem>
                )}
              </InfoGrid>
            )}
          </EventContent>
        </EventCard>

        <Footer>
          <p>
            Evento condiviso tramite <strong style={{ color: '#FF8C00' }}>FINDAY</strong> - La piattaforma per scoprire eventi nella tua città
          </p>
          <p style={{ marginTop: '10px', fontSize: '12px' }}>
            Ultimo aggiornamento: {format(new Date(), 'dd/MM/yyyy HH:mm', { locale: it })}
          </p>
        </Footer>
      </PageContainer>

      {/* Modal per l'immagine ingrandita */}
      {selectedImage && (
        <ImageModal onClick={handleOverlayClick}>
          <CloseButton onClick={handleCloseModal}>
            ×
          </CloseButton>
          <ModalImage 
            src={selectedImage} 
            alt={event?.title || 'Immagine evento'} 
            onClick={(e) => e.stopPropagation()}
          />
        </ImageModal>
      )}
    </>
  );
};

export default EventoCondiviso;
